/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CallFlowGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { GitBranch } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import type { CallFlowFormState } from '../types';

interface CallFlowGeneralSectionProps {
    formData: CallFlowFormState;
    errors: Record<string, string>;
    onFieldChange: <K extends keyof CallFlowFormState>(field: K, value: CallFlowFormState[K]) => void;
    onNameChange: (name: string) => void;
    t: (key: string) => string;
}

export function CallFlowGeneralSection({
    formData, errors, onFieldChange, onNameChange, t,
}: CallFlowGeneralSectionProps) {
    return (
        <FormBox title={t('callFlows.sections.general')} icon={<GitBranch className="h-5 w-5" />}>
            <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">

                {/* Name */}
                <Label htmlFor="name">
                    <SimpleTooltip content={t('callFlows.fields.name.tooltip')}>
                        <span>
                            {t('callFlows.fields.name.label')}
                            <span className="ml-1 text-red-500">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onNameChange(e.target.value)}
                        placeholder={t('callFlows.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Extension */}
                <Label htmlFor="extension">
                    <SimpleTooltip content={t('callFlows.fields.extension.tooltip')}>
                        {t('callFlows.fields.extension.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="extension"
                        value={formData.extension}
                        onChange={(e) => onFieldChange('extension', e.target.value)}
                        placeholder={t('callFlows.fields.extension.placeholder')}
                    />
                </div>

                {/* Status */}
                <Label htmlFor="status">
                    <SimpleTooltip content={t('callFlows.fields.status.tooltip')}>
                        {t('callFlows.fields.status.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.status} onChange={(v) => onFieldChange('status', v)} />
                </div>

                {/* Toggle Enabled */}
                <Label htmlFor="toggleEnabled">
                    <SimpleTooltip content={t('callFlows.fields.toggleEnabled.tooltip')}>
                        {t('callFlows.fields.toggleEnabled.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.toggleEnabled} onChange={(v) => onFieldChange('toggleEnabled', v)} />
                </div>

                {/* Enabled Destination */}
                <Label>
                    <SimpleTooltip content={t('callFlows.fields.enabledDestination.tooltip')}>
                        <span>
                            {t('callFlows.fields.enabledDestination.label')}
                            <span className="ml-1 text-red-500">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div className="col-span-3">
                    <ModuleDestinationSelect
                        moduleValue={formData.enabledDestinationModule}
                        destinationValue={formData.enabledDestinationData}
                        onModuleChange={(v) => onFieldChange('enabledDestinationModule', v || '')}
                        onDestinationChange={(v) => onFieldChange('enabledDestinationData', v || '')}
                        modulePlaceholder={t('callFlows.fields.enabledDestination.modulePlaceholder')}
                        destinationPlaceholder={t('callFlows.fields.enabledDestination.destinationPlaceholder')}
                    />
                    {(errors.enabledDestinationModule || errors.enabledDestinationData) && (
                        <p className="mt-1 text-sm text-red-500">
                            {errors.enabledDestinationModule || errors.enabledDestinationData}
                        </p>
                    )}
                </div>

                {/* Disabled Destination */}
                <Label>
                    <SimpleTooltip content={t('callFlows.fields.disabledDestination.tooltip')}>
                        <span>
                            {t('callFlows.fields.disabledDestination.label')}
                            <span className="ml-1 text-red-500">*</span>
                        </span>
                    </SimpleTooltip>
                </Label>
                <div className="col-span-3">
                    <ModuleDestinationSelect
                        moduleValue={formData.disabledDestinationModule}
                        destinationValue={formData.disabledDestinationData}
                        onModuleChange={(v) => onFieldChange('disabledDestinationModule', v || '')}
                        onDestinationChange={(v) => onFieldChange('disabledDestinationData', v || '')}
                        modulePlaceholder={t('callFlows.fields.disabledDestination.modulePlaceholder')}
                        destinationPlaceholder={t('callFlows.fields.disabledDestination.destinationPlaceholder')}
                    />
                    {(errors.disabledDestinationModule || errors.disabledDestinationData) && (
                        <p className="mt-1 text-sm text-red-500">
                            {errors.disabledDestinationModule || errors.disabledDestinationData}
                        </p>
                    )}
                </div>

                {/* Enabled */}
                <Label htmlFor="enabled">
                    <SimpleTooltip content={t('callFlows.fields.enabled.tooltip')}>
                        {t('callFlows.fields.enabled.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.enabled} onChange={(v) => onFieldChange('enabled', v)} />
                </div>

            </div>
        </FormBox>
    );
}
